// ============================================================================
// stockService — Operational Resource Layer
// ----------------------------------------------------------------------------
// Soft-coupled, transactional. Every mutation runs through Firestore
// transactions so concurrent task completions never over-consume. Audit events
// reuse the existing logAuditEvent transport with a stock.* action namespace.
// ============================================================================

import {
  collection,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  query,
  runTransaction,
  serverTimestamp,
  Timestamp,
  where,
  type DocumentData,
  type Firestore,
} from 'firebase/firestore'

import { firestoreDb } from './firebase'
import { logAuditEvent } from './auditLedgerService'
import { isNoSignInPreviewActive } from '../debug'
import {
  STOCK_COLLECTION,
  type StockAuditAction,
  type StockItem,
  type StockItemDraft,
  type StockRequirement,
} from '../types/stock'
import {
  createStockItems,
  updateStockItemDetails,
  StockLedgerClientError,
  type CreateStockItemEntryInput,
} from './stock/stockLedgerService'

const stockRef = (db: Firestore, stockItemId: string) => doc(db, STOCK_COLLECTION, stockItemId)
const workspaceRef = (db: Firestore, workspaceId: string) => doc(db, 'workspaces', workspaceId)

export const roundToStep = (value: number, step: number = 1): number => {
  if (typeof value !== 'number' || !Number.isFinite(value) || value <= 0) {
    return 0
  }
  const stepHundredths = Math.max(1, Math.round(step * 100))
  const valHundredths = Math.round(value * 100)
  const roundedHundredths = Math.round(valHundredths / stepHundredths) * stepHundredths
  return Math.max(0, roundedHundredths / 100)
}

export const sanitizeQuantity = roundToStep

export const toStockItem = (id: string, data: DocumentData): StockItem => {
  const step = Number(data.quantityStep ?? 1)
  return {
    id,
    workspaceId: String(data.workspaceId ?? ''),
    name: String(data.name ?? ''),
    sku: String(data.sku ?? ''),
    quantity: sanitizeQuantity(Number(data.quantity ?? 0), step),
    reservedQuantity: sanitizeQuantity(Number(data.reservedQuantity ?? 0), step),
    unit: String(data.unit ?? ''),
    location: String(data.location ?? ''),
    status: (data.status as StockItem['status']) ?? 'Available',
    lastUpdated: (data.lastUpdated as Timestamp | null) ?? null,
    linkedTaskIds: Array.isArray(data.linkedTaskIds) ? data.linkedTaskIds.map(String) : [],
    category: data.category != null ? String(data.category) : undefined,
    tags: Array.isArray(data.tags) ? data.tags.map(String) : undefined,
    costPerUnit: data.costPerUnit != null ? Number(data.costPerUnit) : null,
    supplierId: data.supplierId != null ? String(data.supplierId) : null,
    minThreshold: data.minThreshold != null ? Number(data.minThreshold) : null,
    maxThreshold: data.maxThreshold != null ? Number(data.maxThreshold) : null,
    reorderPoint: data.reorderPoint != null ? Number(data.reorderPoint) : null,
    reorderQuantity: data.reorderQuantity != null ? Number(data.reorderQuantity) : null,
    imageUrl: data.imageUrl != null ? String(data.imageUrl) : null,
    barcode: data.barcode != null ? String(data.barcode) : null,
    expiryDate: (data.expiryDate as Timestamp | null) ?? null,
    serialNumbers: Array.isArray(data.serialNumbers) ? data.serialNumbers.map(String) : undefined,
    notes: data.notes != null ? String(data.notes) : undefined,
    quantityStep: (data.quantityStep as 1 | 0.5 | 0.1 | 0.01) ?? 1,
    createdAt: (data.createdAt as Timestamp | null) ?? null,
    updatedAt: (data.updatedAt as Timestamp | null) ?? null,
    createdById: data.createdById != null ? String(data.createdById) : null,
    updatedById: data.updatedById != null ? String(data.updatedById) : null,
  }
}

export type AuditContext = {
  actorId: string
  actorName?: string | null
}

export type StockRequirementReadiness = {
  status: 'READY' | 'SHORTAGE'
  shortages: Array<{
    stockItemId: string
    name: string
    available: number
    required: number
    unit: string
  }>
}

export const evaluateStockRequirementReadiness = (
  requirements: StockRequirement[],
  stockItems: StockItem[],
): StockRequirementReadiness => {
  const stockById = new Map(stockItems.map((item) => [item.id, item]))
  const shortages = requirements.flatMap((requirement) => {
    const item = stockById.get(requirement.stockItemId)
    const step = item?.quantityStep ?? 1
    const required = sanitizeQuantity(requirement.quantity || 1, step)
    const available = item ? Math.max(0, sanitizeQuantity(item.quantity, step) - sanitizeQuantity(item.reservedQuantity, step)) : 0
    return available >= required ? [] : [{
      stockItemId: requirement.stockItemId,
      name: item?.name ?? requirement.stockItemId,
      available,
      required,
      unit: requirement.unitOverride ?? item?.unit ?? '',
    }]
  })
  return { status: shortages.length > 0 ? 'SHORTAGE' : 'READY', shortages }
}

export const emitStockAudit = async (
  db: Firestore,
  workspaceId: string,
  audit: AuditContext,
  action: StockAuditAction,
  stockItemId: string,
  targetTitle: string,
  payload: Record<string, unknown>,
) => {
  try {
    await runTransaction(db, async (transaction) => {
      const wsSnap = await transaction.get(workspaceRef(db, workspaceId))
      const ws = wsSnap.exists() ? wsSnap.data() : undefined
      logAuditEvent(db, transaction, ws, {
        workspaceId,
        actorId: audit.actorId,
        actorName: audit.actorName ?? null,
        action,
        activityType: 'STOCK_MUTATION',
        targetId: stockItemId,
        nodeType: 'STOCK_ITEM',
        targetTitle,
        summary: `Stock ${action.replace('stock.', '')}: ${targetTitle}`,
        payload: { ...payload, action },
      })
    })
  } catch (error) {
    // Audit is best-effort — never block the underlying stock mutation.
    console.warn('[stockService] audit emit failed', { workspaceId, action, stockItemId }, error)
  }
}

// ---------- Subscriptions / fetch ----------

export const subscribeWorkspaceStock = (
  workspaceId: string,
  callback: (items: StockItem[]) => void,
  onError?: (error: Error) => void,
  db: Firestore = firestoreDb,
) => {
  if (!workspaceId) {
    callback([])
    return () => {}
  }
  if (isNoSignInPreviewActive()) {
    callback([])
    return () => {}
  }
  const q = query(collection(db, STOCK_COLLECTION), where('workspaceId', '==', workspaceId))
  return onSnapshot(
    q,
    (snapshot) => {
      const items = snapshot.docs.map((docSnap) => toStockItem(docSnap.id, docSnap.data()))
      callback(items.sort((a, b) => a.name.localeCompare(b.name)))
    },
    (error) => {
      console.warn('[stockService] subscription error', { workspaceId }, error)
      onError?.(error)
    },
  )
}

export const fetchWorkspaceStock = async (
  workspaceId: string,
  db: Firestore = firestoreDb,
): Promise<StockItem[]> => {
  if (!workspaceId) return []
  const q = query(collection(db, STOCK_COLLECTION), where('workspaceId', '==', workspaceId))
  const snapshot = await getDocs(q)
  return snapshot.docs.map((docSnap) => toStockItem(docSnap.id, docSnap.data()))
}

function buildCreateStockItemEntry(draft: StockItemDraft): CreateStockItemEntryInput {
  const entry: CreateStockItemEntryInput = {
    name: draft.name.trim(),
    sku: draft.sku.trim(),
    unit: draft.unit.trim(),
    notes: draft.notes ? draft.notes.trim() : '',
  }
  if (draft.location !== undefined) entry.location = draft.location ? draft.location.trim() : null
  if (draft.quantity !== undefined) entry.openingQuantity = draft.quantity
  if (draft.quantityStep !== undefined) entry.quantityStep = draft.quantityStep
  if (draft.tracking !== undefined) entry.tracking = draft.tracking
  if (draft.category !== undefined) entry.category = draft.category ? draft.category.trim() : null
  if (draft.tags !== undefined) entry.tags = draft.tags
  if (draft.costPerUnit !== undefined) entry.costPerUnit = draft.costPerUnit
  if (draft.supplierId !== undefined) entry.supplierId = draft.supplierId || null
  if (draft.minThreshold !== undefined) entry.minThreshold = draft.minThreshold
  if (draft.maxThreshold !== undefined) entry.maxThreshold = draft.maxThreshold
  if (draft.reorderPoint !== undefined) entry.reorderPoint = draft.reorderPoint
  if (draft.reorderQuantity !== undefined) entry.reorderQuantity = draft.reorderQuantity
  if (draft.imageUrl !== undefined) entry.imageUrl = draft.imageUrl || null
  if (draft.barcode !== undefined) entry.barcode = draft.barcode ? draft.barcode.trim() : null
  if (draft.expiryDate !== undefined) entry.expiryDate = draft.expiryDate
  if (draft.serialNumbers !== undefined) entry.serialNumbers = draft.serialNumbers
  return entry
}

export const createStockItem = async (
  workspaceId: string,
  draft: StockItemDraft,
  _audit: AuditContext,
): Promise<string> => {
  void _audit
  const entry = buildCreateStockItemEntry(draft)

  const ids = await createStockItems(workspaceId, [entry])
  if (!ids[0]) throw new Error('Failed to create stock item')
  return ids[0]
}

export const updateStockItem = async (
  workspaceId: string,
  stockItemId: string,
  patch: Partial<StockItemDraft> & { maintenance?: boolean },
  _audit: AuditContext,
): Promise<void> => {
  void _audit
  if (patch.reservedQuantity !== undefined || patch.status !== undefined) {
    throw new StockLedgerClientError(
      'INVALID_ARGUMENT',
      'Direct updates to reservedQuantity or status are not allowed; status is derived from quantity and maintenance.',
    )
  }

  const allowedMetaKeys = [
    'name',
    'sku',
    'unit',
    'location',
    'category',
    'tags',
    'costPerUnit',
    'supplierId',
    'minThreshold',
    'maxThreshold',
    'reorderPoint',
    'reorderQuantity',
    'imageUrl',
    'barcode',
    'expiryDate',
    'serialNumbers',
    'notes',
    'quantityStep',
    'tracking',
  ] as const

  const metadataPatch: Record<string, unknown> = {}
  for (const key of allowedMetaKeys) {
    if (key in patch && (patch as Record<string, unknown>)[key] !== undefined) {
      metadataPatch[key] = (patch as Record<string, unknown>)[key]
    }
  }

  const options: { targetQuantity?: number; maintenance?: boolean } = {}
  if (patch.quantity !== undefined) options.targetQuantity = patch.quantity
  if (patch.maintenance !== undefined) options.maintenance = Boolean(patch.maintenance)

  if (Object.keys(metadataPatch).length > 0 || options.targetQuantity !== undefined || options.maintenance !== undefined) {
    await updateStockItemDetails(
      workspaceId,
      stockItemId,
      metadataPatch,
      options,
    )
  }
}

export const deleteStockItem = async (
  workspaceId: string,
  stockItemId: string,
  audit: AuditContext,
  db: Firestore = firestoreDb,
): Promise<void> => {
  let nameForAudit = stockItemId
  await runTransaction(db, async (transaction) => {
    const ref = stockRef(db, stockItemId)
    const snap = await transaction.get(ref)
    if (!snap.exists()) return
    const current = snap.data()
    if (current.workspaceId !== workspaceId) throw new Error('Workspace mismatch on stock delete')
    nameForAudit = String(current.name ?? stockItemId)
    transaction.delete(ref)
  })
  await emitStockAudit(db, workspaceId, audit, 'stock.delete', stockItemId, nameForAudit, {})
}

// ---------- Reserve / consume / release ----------

// ---------- Link sync (Task.requiredStockIds → StockItem.linkedTaskIds) ----------

export const syncStockTaskLinks = async (
  workspaceId: string,
  taskId: string,
  beforeIds: string[],
  afterIds: string[],
  db: Firestore = firestoreDb,
) => {
  if (!workspaceId || !taskId) return
  const beforeSet = new Set(beforeIds.filter(Boolean))
  const afterSet = new Set(afterIds.filter(Boolean))
  const added = [...afterSet].filter((id) => !beforeSet.has(id))
  const removed = [...beforeSet].filter((id) => !afterSet.has(id))
  if (added.length === 0 && removed.length === 0) return

  const touched = new Set<string>([...added, ...removed])
  await Promise.all(
    [...touched].map(async (stockItemId) => {
      try {
        await runTransaction(db, async (transaction) => {
          const ref = stockRef(db, stockItemId)
          const snap = await transaction.get(ref)
          if (!snap.exists()) return
          const data = snap.data()
          if (data.workspaceId !== workspaceId) return
          const current: string[] = Array.isArray(data.linkedTaskIds) ? data.linkedTaskIds.map(String) : []
          const next = added.includes(stockItemId)
            ? (current.includes(taskId) ? current : [...current, taskId])
            : current.filter((id) => id !== taskId)
          transaction.update(ref, { linkedTaskIds: next, lastUpdated: serverTimestamp() })
        })
      } catch (error) {
        // Soft-fail. The link is denormalised — Task.requiredStockIds remains the source of truth.
        console.warn('[stockService] linkedTaskIds sync failed', { stockItemId, taskId }, error)
      }
    }),
  )
}

// ---------- Helpers for UI ----------

export const fetchStockItem = async (
  stockItemId: string,
  db: Firestore = firestoreDb,
): Promise<StockItem | null> => {
  const snap = await getDoc(stockRef(db, stockItemId))
  if (!snap.exists()) return null
  return toStockItem(snap.id, snap.data())
}

